import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Icon,
  OutlinedInput,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DateTimeInput from "components/MDDateTimeInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { alpha } from "@mui/material/styles";
import { AddDevoteeAPI } from "APIs/DevoteeAPIs";
import { GetCountryCodeList } from "APIs/CommonAPI";
import { UserContext, notify } from "App";
import FullScreenLoader from "components/Custom/FullScreenLoader";
// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
} from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

const DEFAULT_COUNTRY = { value: 112, label: "INDIA  :  +91" };

const ERROR_RED = "red";

// Red border helper, used for every field that has an error
const errorBorderSx = (error) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? ERROR_RED : undefined,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? ERROR_RED : undefined,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? ERROR_RED : undefined,
  },
});

// Back button: maroon (theme primary) outline and text, arrow icon on the left
const backButtonSx = ({ palette }) => ({
  color: palette.primary.main,
  borderColor: palette.primary.main,
  backgroundColor: "transparent",
  borderRadius: "10px",
  boxShadow: "none",
  boxSizing: "border-box",
  height: { xs: "2.25rem", md: "2.5rem" },
  minHeight: { xs: "2.25rem", md: "2.5rem" },
  px: 2.5,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  gap: 0.75,
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  // Hover: gold (theme secondary), same as the other outlined buttons
  "&:hover": {
    color: palette.secondary.main,
    borderColor: palette.secondary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "add-devotee-form";

// Render order of the form. Change the order here to change the layout.
const FIELDS = [
  { name: "first_name", required: true },
  { name: "middle_name" },
  { name: "last_name", required: true },
  { name: "phone", type: "phone", required: true },
  { name: "email" },
  { name: "gender", type: "gender" },
  { name: "birth_date", type: "date" },
  { name: "anniversary_date", type: "date" },
  { name: "comments" },
];

const INITIAL_DATA = {
  first_name: "",
  middle_name: "",
  last_name: "",
  phone: "",
  email: "",
  gender: "",
  birth_date: "",
  anniversary_date: "",
  comments: "",
};

const getTodayISO = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

// Stored value is dd-mm-yyyy; flip to yyyy-mm-dd so plain string comparison works
const isNotFutureDate = (value) => value.split("-").reverse().join("-") <= getTodayISO();

// Format rules, checked only when the field has a value
const VALIDATORS = {
  phone: {
    isValid: (v) => v.length === 10,
    message: "Phone number must be exactly 10 digits",
  },
  email: {
    isValid: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    message: "Please enter a valid email address",
  },
  birth_date: {
    isValid: isNotFutureDate,
    message: "Birth date cannot be in the future",
  },
  anniversary_date: {
    isValid: isNotFutureDate,
    message: "Anniversary date cannot be in the future",
  },
};

// Swaps yyyy-mm-dd <-> dd-mm-yyyy (the API stores dd-mm-yyyy, <input type="date"> uses yyyy-mm-dd)
const flipDate = (value) => value.split("-").reverse().join("-");

const labelOf = (name) => name.split("_").join(" ");

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// gender uses 0 for Male, so check for "empty" explicitly instead of using falsy
const isEmpty = (value) => value === "" || value === null || value === undefined;

// Optional fields: when empty they are left out of the request instead of being sent as ""
const OMIT_WHEN_EMPTY = ["gender", "birth_date", "anniversary_date", "email"];

const buildPayload = (data, countryCode) => ({
  ...Object.fromEntries(
    Object.entries(data).filter(([key, value]) => !OMIT_WHEN_EMPTY.includes(key) || !isEmpty(value))
  ),
  country_code: countryCode,
});

const AddDevotee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useContext(UserContext);

  const [devoteeData, setDevoteeData] = useState(INITIAL_DATA);
  const [touched, setTouched] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCountries = async () => {
      const response = await GetCountryCodeList(userData.access_token);
      setCountryList(
        response.map((country) => ({
          value: country.country_currency_id,
          label: `${country.country_name}  :  ${country.phone_code}`,
        }))
      );
    };
    loadCountries();
  }, [userData]);

  const handleChange = (field, value) => setDevoteeData((prev) => ({ ...prev, [field]: value }));

  const handleBlur = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  // Real validation state: checked when Save is clicked
  const getError = ({ name, required }) => {
    const value = devoteeData[name];

    if (required && (isEmpty(value) || String(value).trim() === "")) {
      return `${capitalize(labelOf(name))} is required`;
    }

    const rule = VALIDATORS[name];
    if (rule && !isEmpty(value) && !rule.isValid(value)) {
      return rule.message;
    }

    return "";
  };

  // What the user sees: an error only appears after the field has been blurred
  const getVisibleError = (field) => (touched[field.name] ? getError(field) : "");

  const isFormValid = FIELDS.every((field) => !getError(field));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Invalid form: mark every field as touched so all missing / wrong fields turn red
    if (!isFormValid) {
      setTouched(Object.fromEntries(FIELDS.map((field) => [field.name, true])));
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload(devoteeData, Number(selectedCountry.value));
      const response = await AddDevoteeAPI(payload, userData.access_token, dispatch);
      if (response.is_success) {
        navigate(`/devotee/new_address/${response.data.devotee_id}`);
        notify("Devotee added successfully", "success");
      } else {
        notify(response.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (field) => {
    const { name, type } = field;
    const isDate = type === "date";
    const error = getVisibleError(field);

    return (
      <>
        {/* Wrapper forces a red border on DateTimeInput whenever there is an error */}
        <MDBox
          sx={{
            "& .MuiOutlinedInput-notchedOutline, & fieldset": {
              borderColor: error ? `${ERROR_RED} !important` : undefined,
            },
            "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
              borderColor: error ? `${ERROR_RED} !important` : undefined,
            },
          }}
        >
          <DateTimeInput
            type={isDate ? "date" : "text"}
            // Greys out future dates in the picker (validation still catches typed dates)
            inputProps={isDate ? { max: getTodayISO() } : undefined}
            autoComplete="off"
            value={isDate ? flipDate(devoteeData[name]) : devoteeData[name]}
            onChange={(e) => {
              const value = isDate ? flipDate(e.target.value) : e.target.value;
              handleChange(name, value);
            }}
            onBlur={() => handleBlur(name)}
            error={!!error}
            placeholder={isDate ? `Select ${labelOf(name)}` : `Enter ${labelOf(name)}`}
            fullWidth
          />
        </MDBox>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </>
    );
  };

  const renderGender = (field) => {
    const { name } = field;
    const error = getVisibleError(field);

    return (
      <>
        <FormControl fullWidth error={!!error}>
          <Select
            style={{ width: "100%", height: "45px" }}
            sx={errorBorderSx(error)}
            value={devoteeData[name]}
            displayEmpty
            onChange={(e) => {
              handleChange(name, e.target.value);
              handleBlur(name);
            }}
            onBlur={() => handleBlur(name)}
          >
            <MenuItem value="" disabled>
              Select Gender
            </MenuItem>
            <MenuItem value={0}>Male</MenuItem>
            <MenuItem value={1}>Female</MenuItem>
          </Select>
        </FormControl>

        {error && <FormHelperText error>{error}</FormHelperText>}
      </>
    );
  };

  const renderPhone = (field) => {
    const { name } = field;
    const error = getVisibleError(field);

    return (
      <>
        <FormControl fullWidth error={!!error}>
          <OutlinedInput
            autoComplete="off"
            placeholder="Enter phone"
            value={devoteeData[name]}
            onChange={(e) => handleChange(name, e.target.value.replace(/\D/g, "").slice(0, 10))}
            onBlur={() => handleBlur(name)}
            sx={{ height: "45px", paddingLeft: 0, ...errorBorderSx(error) }}
            startAdornment={
              // Highlighted country-code block: tinted background, centered, line on the right
              <MDBox
                sx={({ palette }) => ({
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "95px",
                  mr: 1.5,
                  backgroundColor: alpha(palette.primary.main, 0.08),
                  borderRight: `1px solid ${alpha(palette.primary.main, 0.35)}`,
                  borderRadius: "9px 0 0 9px",
                })}
              >
                <Select
                  variant="standard"
                  disableUnderline
                  value={selectedCountry.value}
                  onChange={(e) => {
                    const selected = countryList.find(
                      (country) => country.value === e.target.value
                    );
                    if (selected) setSelectedCountry(selected);
                  }}
                  // Box shows only the code (+91); the dropdown shows "INDIA : +91"
                  renderValue={() => selectedCountry.label.split(":").pop().trim()}
                  sx={({ palette }) => ({
                    width: "100%",
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 0,
                      pl: 1.5,
                      fontWeight: 700,
                      color: palette.primary.main,
                      textAlign: "center",
                      backgroundColor: "transparent !important",
                    },
                    "& .MuiSelect-icon": { color: palette.primary.main },
                  })}
                >
                  {countryList.length === 0 && (
                    <MenuItem value={selectedCountry.value}>{selectedCountry.label}</MenuItem>
                  )}
                  {countryList.map((country) => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
              </MDBox>
            }
          />
        </FormControl>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </>
    );
  };
  const renderControl = (field) => {
    switch (field.type) {
      case "gender":
        return renderGender(field);
      case "phone":
        return renderPhone(field);
      default:
        return renderInput(field);
    }
  };

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      {/* Back button sits right beside the page badge (startAction = left of the badge) */}
      <SectionHeader
        icon="group_add"
        label="ADD DEVOTEE"
        startAction={
          <MDButton
            variant="outlined"
            color="primary"
            sx={backButtonSx}
            onClick={() => navigate("/devotee")}
          >
            <Icon sx={{ fontSize: "1.1rem !important", fontWeight: "bold" }}>arrow_back</Icon>
            Back
          </MDButton>
        }
      />

      <SectionCard title="Devotee" subtitle="Fields marked * are required">
        <form id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
          <Grid container spacing={2}>
            {FIELDS.map((field) => (
              <FormField key={field.name} name={field.name} required={field.required}>
                {renderControl(field)}
              </FormField>
            ))}
          </Grid>
        </form>
      </SectionCard>

      <MDBox mt={2} display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
        <MDButton
          variant="outlined"
          color="primary"
          sx={ranuOutlinedButtonSx}
          disabled={loading}
          onClick={() => navigate("/devotee")}
        >
          Cancel
        </MDButton>
        <MDButton type="submit" form={FORM_ID} color="primary" sx={ranuButtonSx} disabled={loading}>
          Save Devotee
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default AddDevotee;
